import { inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, firstValueFrom, of } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';

import { CustomOverlayRef } from '@shared/components/overlay/custom-overlay-ref';
import { CommonConstants } from '@shared/constants/common.constants';
import { LocalStorageKey } from '@shared/constants/localStorageKey';
import { ROUTING_PATH } from '@shared/constants/routingPath';
import { EntityType, EventType, GqlService, RoleType } from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { ScriptLoaderService } from '@shared/services/script-loader.service';
import { AuthQuery } from '@shared/store/auth/auth.query';
import { AuthService } from '@shared/store/auth/auth.service';
import { EventService } from '@shared/store/event/event.service';
import { TrialUserService } from '@shared/store/trial-users/trial-user.service';
import { TrialsQuery } from '@shared/store/trials/trials.query';
import { TrialsService } from '@shared/store/trials/trials.service';
import { RegExpUtils } from '@shared/utils/regExp.utils';

import { MainStore } from './main.store';

@Injectable({ providedIn: 'root' })
export class MainService {
  private authQuery = inject(AuthQuery);
  private authService = inject(AuthService);
  private eventService = inject(EventService);
  private gqlService = inject(GqlService);
  private mainStore = inject(MainStore);
  private overlayService = inject(OverlayService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private scriptLoaderService = inject(ScriptLoaderService);
  private trialsQuery = inject(TrialsQuery);
  private trialsService = inject(TrialsService);
  private trialUserService = inject(TrialUserService);

  setTrialIsLoading = new BehaviorSubject(false);

  closeLoadingOverlay(overlay: CustomOverlayRef) {
    overlay.close();
    this.setTrialIsLoading.next(false);
  }

  constructor() {
    this.authQuery.isLoggedIn$
      .pipe(
        takeUntilDestroyed(),
        switchMap((x: boolean) => {
          if (x) {
            this.mainStore.setLoading(true);

            return this.trialsService.get().pipe(
              // eslint-disable-next-line @typescript-eslint/no-misused-promises
              tap(async ({ data }) => {
                if (data?.length) {
                  let trialKey: string;
                  const { queryParams } = this.route.snapshot;
                  const trialId = queryParams[CommonConstants.TRIAL_ID_QUERY_PARAMETER_NAME];
                  const isTrialFromQueryParameterExisting = !!data.filter((d) => d.id === trialId)
                    .length;

                  if (trialId && isTrialFromQueryParameterExisting) {
                    trialKey = trialId;
                  } else if (trialId && !isTrialFromQueryParameterExisting) {
                    trialKey = this.authQuery.getValue().trial_id;
                    void this.router.navigate([], { queryParams: {}, replaceUrl: true });
                  } else {
                    trialKey = this.authQuery.getValue().trial_id;
                  }

                  const selectedTrial = data.find((trial) => trial.id === trialKey) || data[0];

                  await this.setTrial(selectedTrial.id, false);
                } else {
                  this.mainStore.update({ trialKey: '' });
                }

                this.mainStore.setLoading(false);
              })
            );
          }

          this.mainStore.setLoading(false);

          return of([]);
        })
      )
      .subscribe();

    this.authQuery.isLoggedIn$.pipe(takeUntilDestroyed()).subscribe((isLoggedIn) => {
      if (isLoggedIn) {
        void this.scriptLoaderService.injectJiraWidget();
      }
    });
  }

  getOpenMonth$() {
    return this.gqlService.currentOpenBudgetMonth$().pipe(
      tap(({ data }) => {
        if (!data) {
          return;
        }

        const currentOpenMonth = data.length ? data[0].trial_month_close : '';
        const trialStartDate = data.length ? data[0].trial_start_date : '';
        const trialEndDate = data.length ? data[0].trial_end_date : '';

        this.mainStore.update((state) => ({
          ...state,
          currentOpenMonth,
          trialEndDate,
          trialStartDate,
        }));
      })
    );
  }

  getUserList$() {
    return this.gqlService.listUserNamesWithEmail$(true).pipe(
      tap(({ data }) => {
        this.mainStore.update((state) => ({
          ...state,
          userList: data || [],
        }));
      })
    );
  }

  private redirectOnTrialChange(): void {
    const prepaidsPage = `/${ROUTING_PATH.CLOSING.INDEX}/${ROUTING_PATH.CLOSING.INVOICES_AND_PREPAIDS.INDEX}/${ROUTING_PATH.CLOSING.INVOICES_AND_PREPAIDS.PREPAIDS}/`;
    const uuidLength = 36;
    const uuidToTest = this.router.url.slice(prepaidsPage.length, prepaidsPage.length + uuidLength);
    const isPrepaidDetailsPage =
      this.router.url.startsWith(prepaidsPage) && RegExpUtils.isUUID(uuidToTest);

    if (isPrepaidDetailsPage) {
      void this.router.navigateByUrl(prepaidsPage.slice(0, -1));
    }
  }

  async sendEmailAfterSetTrialIfFirstTimeLogin() {
    const loggedInUser = this.authQuery.getValue();

    if (localStorage.getItem(LocalStorageKey.SEND_PASSWORD_UPDATED_EMAIL)) {
      await firstValueFrom(
        this.eventService.processEvent$({
          entity_id: '',
          entity_type: EntityType.USER,
          payload: JSON.stringify({
            password_changed_for: `${loggedInUser.email}`,
          }),
          type: EventType.USER_PASSWORD_UPDATED,
        })
      );

      localStorage.removeItem(LocalStorageKey.SEND_PASSWORD_UPDATED_EMAIL);
    }
  }

  async setTrial(value: string, showLoading = true) {
    if (value) {
      const ref = showLoading && this.showLoadingOverlay();

      this.mainStore.update({ isTrialLoading: true });

      try {
        const { errors, success } = await firstValueFrom(this.gqlService.setTrial$(value));

        if (!success) {
          this.overlayService.error(errors);
        } else {
          await this.authService.setUserAttributes({ forceRefresh: true });
        }
      } catch (e) {
        console.error('Unable to refresh Token', e);
      }

      const loggedInUser = this.authQuery.getValue();
      const trial = this.trialsQuery.getEntity(value);

      if (loggedInUser.is_admin && trial) {
        const { errors, success } = await this.trialUserService.addToTrial({
          department: loggedInUser.department || [],
          family_name: loggedInUser.family_name,
          given_name: loggedInUser.given_name,
          role: RoleType.ROLE_ADMIN,
          title: loggedInUser?.title || 'Administrator',
          trial_id: value,
          user_email: loggedInUser.email,
          user_id: loggedInUser.sub,
        });

        if (!success) {
          this.overlayService.error(errors);
        }
      }

      if (ref) {
        this.closeLoadingOverlay(ref);
      }

      // if the user first time logging in then send the email after setTrial
      await this.sendEmailAfterSetTrialIfFirstTimeLogin();

      const fromTrialKey = this.mainStore.getValue().trialKey;

      await this.mainStore.setTrial();
      this.mainStore.update({ isTrialLoading: false });
      const toTrialKey = this.mainStore.getValue().trialKey;
      const shouldApplyRedirections = fromTrialKey !== toTrialKey && !!fromTrialKey;

      if (shouldApplyRedirections) {
        this.redirectOnTrialChange();
      }
    }
  }

  showLoadingOverlay() {
    this.setTrialIsLoading.next(true);

    return this.overlayService.loading();
  }
}
